import React, { useState } from 'react';
import { useDrag } from 'react-dnd';

const ToolboxItem = ({ type, description }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'COMPONENT',
    item: { type },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  }));

  return (
    <div
      ref={drag}
      className={`p-2 border rounded cursor-move bg-white shadow-sm hover:bg-gray-50 ${
        isDragging ? 'opacity-50' : ''
      }`}
      title={description}
    >
      <div className="font-medium text-sm">{type}</div>
      <div className="text-xs text-gray-500">{description}</div>
    </div>
  );
};

const ToolboxGroup = ({ title, components, isExpanded, onToggle }) => {
  return (
    <div className="border rounded">
      <button
        onClick={onToggle}
        className="w-full p-2 bg-gray-100 hover:bg-gray-200 text-left font-medium text-sm flex items-center justify-between"
      >
        <span>{title}</span>
        <span className="text-gray-500">{isExpanded ? '▼' : '▶'}</span>
      </button>
      {isExpanded && (
        <div className="p-2 space-y-1">
          {components.map(component => (
            <ToolboxItem 
              key={component.type} 
              type={component.type} 
              description={component.description}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function Toolbox() {
  const [expandedGroups, setExpandedGroups] = useState({
    containers: false,
    common: false,
    input: false,
    display: false,
    powershell: false,
    dialogs: false,
    advanced: false
  });

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const componentGroups = {
    containers: {
      title: "Containers",
      components: [
        { type: 'Form', description: 'Form container for components' },
        { type: 'Panel', description: 'Container panel' },
        { type: 'GroupBox', description: 'Container with title' },
        { type: 'TabControl', description: 'Tabbed interface' },
        { type: 'SplitContainer', description: 'Resizable split panels' },
      ]
    },
    common: {
      title: "Common Controls",
      components: [
        { type: 'Button', description: 'Clickable button' },
        { type: 'Label', description: 'Static text display' },
        { type: 'LinkLabel', description: 'Clickable link text' },
        { type: 'Separator', description: 'Visual separator line' },
      ]
    },
    input: {
      title: "Input Controls",
      components: [
        { type: 'TextBox', description: 'Text input field' },
        { type: 'Checkbox', description: 'Boolean selection' },
        { type: 'RadioButton', description: 'Single choice selection' },
        { type: 'Dropdown', description: 'Selection from options' },
        { type: 'ComboBox', description: 'Editable dropdown with suggestions' },
        { type: 'ListBox', description: 'List of selectable items' },
        { type: 'CheckedListBox', description: 'List with checkboxes' },
        { type: 'NumericUpDown', description: 'Number input with arrows' },
        { type: 'Slider', description: 'Numeric value selector' },
        { type: 'DateTimePicker', description: 'Date/time selection' },
        { type: 'MonthCalendar', description: 'Month calendar view' },
      ]
    },
    display: {
      title: "Display Controls",
      components: [
        { type: 'ProgressBar', description: 'Progress indicator' },
        { type: 'RichTextBox', description: 'Multi-line text editor' },
        { type: 'PictureBox', description: 'Image display' },
        { type: 'TreeView', description: 'Hierarchical data display' },
        { type: 'DataGrid', description: 'Table/grid display' },
        { type: 'ListView', description: 'Multi-column list display' },
      ]
    },
    powershell: {
      title: "PowerShell Controls",
      components: [
        { type: 'CommandOutput', description: 'PowerShell output display' },
        { type: 'ParameterPanel', description: 'Dynamic parameter input' },
      ]
    },
    dialogs: {
      title: "Dialog Controls",
      components: [
        { type: 'ColorDialog', description: 'Color picker button' },
        { type: 'OpenFileDialog', description: 'File open button' },
        { type: 'SaveFileDialog', description: 'File save button' },
        { type: 'FolderBrowserDialog', description: 'Folder browser button' },
        { type: 'PrintDialog', description: 'Print dialog button' },
        { type: 'FontDialog', description: 'Font picker button' },
      ]
    },
    advanced: {
      title: "Advanced Controls",
      components: [
        { type: 'MenuStrip', description: 'Application menu' },
        { type: 'ToolStrip', description: 'Toolbar with buttons' },
        { type: 'StatusStrip', description: 'Status bar' },
        { type: 'Timer', description: 'Background timer' },
        { type: 'NotifyIcon', description: 'System tray icon' },
      ]
    }
  };

  return (
    <div className="w-48 flex flex-col h-full">
      <h2 className="font-semibold mb-2">Toolbox</h2>
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {Object.entries(componentGroups).map(([groupKey, group]) => (
          <ToolboxGroup
            key={groupKey}
            title={group.title}
            components={group.components}
            isExpanded={expandedGroups[groupKey]}
            onToggle={() => toggleGroup(groupKey)}
          />
        ))}
      </div>
    </div>
  );
}
